import { useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import { SkipValidationsDisabledReason } from '@/api';
import { PriceWithDetailsTooltipWithConversions } from '@/components/PriceWithDetails/PriceWithDetails';
import WorkshopPriceLabel from '@/components/WorkshopPriceLabel/WorkshopPriceLabel';
import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import InputText from '@/design_system/InputText';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Toggle from '@/design_system/Toggle';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconEdit from '@/icons/Edit.svg';
import { getGroupedActions, useGetActionName } from '@/models/actionType';
import { useAnalysisValidations, useAnalyze } from '@/models/article';
import { getGroupedDefects, useGetDefectName } from '@/models/defectType';
import { ArticleWithRelations, RequestWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { areArticleSnashotsEqual } from '@/utils/articleSnapshot';
import { useGetExchangeTypes } from '@/utils/exchangeTypes';

export const AnalyzeConfirmationDialog = ({
  article,
  isOpen,
  setIsOpen,
  onActionDone,
}: {
  article: ArticleWithRelations;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onActionDone: () => void;
}) => {
  const { t } = useLingui();

  return (
    <Dialog
      title={t({
        id: 'analysis-confirmation-modal.title',
        message: 'Review and submit quote',
      })}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <AnalyzeConfirmationForm
        article={article}
        onActionDone={onActionDone}
        onClose={() => setIsOpen(false)}
      />
    </Dialog>
  );
};

const AnalyzeConfirmationForm = ({
  article,
  onActionDone,
  onClose,
}: {
  article: ArticleWithRelations;
  onActionDone: () => void;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const [comment, setComment] = useState<string | undefined>();
  const [isSkipValidationsToggled, setIsSkipValidationsToggled] = useState(false);
  const [externalQuoteReference, setExternalQuoteReference] = useState<string | undefined>();

  const [apiError, setApiError] = useState<string | null>(null);
  const { request, workflow, view: requestView } = useRequestContext();
  const { exchangeTypeOptions } = useGetExchangeTypes();

  const getDefectName = useGetDefectName();
  const getActionName = useGetActionName();

  const { mutateAsync: analyzeArticle, isPending: isAnalyzePending } = useAnalyze({
    articleId: article.id,
  });

  const { data: analysisValidations, isLoading: isLoadingAnalysisValidations } =
    useAnalysisValidations({ articleId: article.id });

  const isRequalificationValidationRequired =
    !!analysisValidations?.requalification.enabled &&
    !analysisValidations.requalification.autoAccepted;
  const isClientValidationRequired =
    !!analysisValidations?.client.enabled && !analysisValidations.client.autoAccepted;
  const isValidationRequired = isRequalificationValidationRequired || isClientValidationRequired;

  const submit = async () => {
    try {
      await analyzeArticle({
        comment,
        externalQuoteReference,
        skipRequalificationAndClientValidation: analysisValidations?.skipValidations.enabled
          ? isSkipValidationsToggled
          : undefined,
      });
      onClose();
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const repairStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'repair');

  const getDefaultWorkflowValue = (request: RequestWithRelations) => {
    const dueAtType = request?.dueAtType;

    return repairStep?.config?.statusDueAt?.repair.find(({ type }) => type === dueAtType)?.days;
  };

  const defaultWorkflowValue = getDefaultWorkflowValue(request);

  const groupedDefects = getGroupedDefects(article.snapshot.articleDefects);
  const groupedActions = getGroupedActions(article.snapshot.articleActions);

  const getServiceLabel = () => {
    switch (article.snapshot.serviceChoice) {
      case 'exchange':
        return (
          exchangeTypeOptions.find(
            (option) => option.value === article.snapshot.articleExchange?.type
          )?.label ??
          t({
            id: 'service-choice.radio.exchange.label',
            message: 'Exchange',
          })
        );

      case 'exchange-archival':
        return t({ id: 'service-choice.radio.exchange.label', message: 'Exchange' });
      case 'refund':
        return t({ id: 'service-choice.radio.refund.label', message: 'Refund' });
      case 'none':
        return t({
          id: 'service-choice.radio.none.sub-text',
          message: 'No service can be provided',
        });
      case 'care-repair':
      default:
        return t({ id: 'service-choice.radio.care-repair.label', message: 'Care and repair' });
    }
  };

  const noVATApplied = request?.workshopPrice?.amountPerEntity?.some((a) => a.noVATApplied);

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Paragraph color="secondary">
            <Trans id="analysis-confirmation-modal.intro">
              Please review your quote and make any necessary changes before submitting it.
            </Trans>
          </Paragraph>

          <AlertBar hideIcon>
            <ul>
              {article.snapshot.serviceChoice &&
                article.snapshot.serviceChoice !== 'care-repair' && (
                  <li>
                    <InlineText color="secondary">
                      <Trans id="analysis-confirmation-modal.service">Service</Trans>
                      <Trans id="_general.colon">:</Trans>{' '}
                    </InlineText>
                    <InlineText weight="medium">{getServiceLabel()}</InlineText>
                  </li>
                )}

              {groupedDefects.length > 0 && (
                <li>
                  <InlineText color="secondary">
                    <Trans id="analysis-confirmation-modal.defects">Defects</Trans>
                    <Trans id="_general.colon">:</Trans>{' '}
                  </InlineText>
                  <InlineText weight="medium">
                    {groupedDefects
                      .map(
                        (defect) =>
                          `${getDefectName(defect)}${defect.quantity > 1 ? ` (x${defect.quantity})` : ''}`
                      )
                      .join(', ')}
                  </InlineText>
                </li>
              )}

              {article.snapshot.serviceChoice === 'care-repair' && groupedActions.length > 0 && (
                <li>
                  <InlineText color="secondary">
                    <Trans id="analysis-confirmation-modal.actions">Actions</Trans>
                    <Trans id="_general.colon">:</Trans>{' '}
                  </InlineText>
                  <InlineText weight="medium">
                    {groupedActions
                      .map(
                        (action) =>
                          `${getActionName({ action })}${action.quantity > 1 ? ` (x${action.quantity})` : ''}`
                      )
                      .join(', ')}
                  </InlineText>
                </li>
              )}

              {requestView.workshopPrice.shown &&
                article.snapshot.workshopPrice?.amountPerCurrency?.[0] && (
                  <li>
                    <Stack row gap=".3em">
                      <Stack row>
                        <WorkshopPriceLabel noVATApplied={noVATApplied} />
                        <Trans id="_general.colon">:</Trans>
                      </Stack>
                      <PriceWithDetailsTooltipWithConversions
                        price={article.snapshot.workshopPrice.amountPerCurrency[0]}
                        options={{
                          showAmountBeforeTaxes: requestView.workshopPrice.showAmountBeforeTaxes,
                        }}
                        size="small"
                      />
                    </Stack>
                  </li>
                )}

              {requestView.organizationPrice.shown &&
                article.snapshot.organizationPrice?.amountPerCurrency?.[0] && (
                  <li>
                    <InlineText color="secondary">
                      {requestView.organizationPrice.label}
                      <Trans id="_general.colon">:</Trans>{' '}
                    </InlineText>
                    <PriceWithDetailsTooltipWithConversions
                      price={article.snapshot.organizationPrice.amountPerCurrency[0]}
                      options={{
                        showAmountBeforeTaxes: requestView.organizationPrice.showAmountBeforeTaxes,
                      }}
                      size="small"
                    />
                  </li>
                )}

              {article.snapshot.serviceChoice === 'care-repair' &&
                repairStep?.config?.editRepairDueDays?.enabled && (
                  <li>
                    <InlineText color="secondary">
                      <Trans id="repair-duration.label">Repair duration (est.)</Trans>
                      <Trans id="_general.colon">:</Trans>{' '}
                    </InlineText>
                    <InlineText weight="medium">
                      <Plural
                        id="repair-duration.days"
                        value={article.manualRepairDueDays ?? defaultWorkflowValue!}
                        one="# day"
                        other="# days"
                      />
                    </InlineText>
                  </li>
                )}

              {/* TODO(TECH-1962): Add exchange duration when backend supports it */}
            </ul>
          </AlertBar>

          {!!analysisValidations && (
            <Stack gap="0.5rem">
              <Stack row justifyContent="space-between">
                <InlineText weight="medium">
                  <Trans id="analysis-confirmation-modal.quote-validation.label">
                    Quote validation
                  </Trans>
                </InlineText>

                <SkipValidationToggle
                  skipValidations={analysisValidations.skipValidations}
                  isSkipValidationsToggled={isSkipValidationsToggled}
                  setIsSkipValidationsToggled={setIsSkipValidationsToggled}
                />
              </Stack>

              <QuoteValidationInfo
                isRequalificationValidationRequired={isRequalificationValidationRequired}
                isClientValidationRequired={isClientValidationRequired}
                isSkipValidationsToggled={isSkipValidationsToggled}
              />
            </Stack>
          )}

          {!!article.workshop?.workshopConfig?.nonDigitalized && (
            <InputText
              label={
                <>
                  <Trans id="analysis-confirmation-modal.external-quote-reference">
                    External quote reference
                  </Trans>
                  <OptionalLabel />
                </>
              }
              placeholder={t({
                id: 'analysis-confirmation-modal.external-quote-reference.placeholder',
                message: 'Add reference',
              })}
              value={externalQuoteReference}
              onChange={setExternalQuoteReference}
            />
          )}

          {!areArticleSnashotsEqual(article) && (
            <TextArea
              label={
                <>
                  <Trans id="analysis-confirmation-modal.note">Note</Trans>
                  <OptionalLabel />
                </>
              }
              placeholder={t({
                id: 'analysis-confirmation-modal.placeholder',
                message: 'Explain changes for the coordinator',
              })}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </Stack>
      </main>

      <Footer>
        {!!apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={onClose} disabled={isAnalyzePending}>
            <IconEdit />
            <Trans id="analysis-confirmation-modal.edit">Edit quote</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={submit}
            isLoading={isAnalyzePending}
            disabled={isLoadingAnalysisValidations}
          >
            {!analysisValidations ? (
              <Trans id="article.analysis.footer.action.validate">Submit quote</Trans>
            ) : isValidationRequired ? (
              <Trans id="analysis-confirmation-modal.submit-for-approval">
                Submit for approval
              </Trans>
            ) : (
              <Trans id="analysis-confirmation-modal.submit-proposal">Submit proposal</Trans>
            )}
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};

const QuoteValidationInfo = ({
  isRequalificationValidationRequired,
  isClientValidationRequired,
  isSkipValidationsToggled,
}: {
  isRequalificationValidationRequired: boolean;
  isClientValidationRequired: boolean;
  isSkipValidationsToggled: boolean;
}) => {
  const { workflow } = useRequestContext();

  const acceptRequalificationTraits = workflow?.nodes.find(
    (node) => 'step' in node && node.step === 'analysis'
  )?.config?.tasks?.accept_requalification?.traits;
  const isValidatedByStore = !!acceptRequalificationTraits?.includes('store');

  const getMessage = () => {
    if (
      isRequalificationValidationRequired &&
      isClientValidationRequired &&
      !isSkipValidationsToggled
    ) {
      return isValidatedByStore ? (
        <Trans id="analysis-confirmation-modal.quote-validation.store-and-client">
          The{' '}
          <InlineText weight="medium" color="primary">
            store
          </InlineText>{' '}
          and the{' '}
          <InlineText weight="medium" color="primary">
            client
          </InlineText>{' '}
          will review the quote.{' '}
          <InlineText weight="medium" color="primary">
            No service can start without full approval.
          </InlineText>
        </Trans>
      ) : (
        <Trans id="analysis-confirmation-modal.quote-validation.after-sales-and-client">
          The{' '}
          <InlineText weight="medium" color="primary">
            after-sales team
          </InlineText>{' '}
          and the{' '}
          <InlineText weight="medium" color="primary">
            client
          </InlineText>{' '}
          will review the quote.{' '}
          <InlineText weight="medium" color="primary">
            No service can start without full approval.
          </InlineText>
        </Trans>
      );
    }

    if (isRequalificationValidationRequired && !isSkipValidationsToggled) {
      return isValidatedByStore ? (
        <Trans id="analysis-confirmation-modal.quote-validation.store">
          The{' '}
          <InlineText weight="medium" color="primary">
            store
          </InlineText>{' '}
          will review the quote.{' '}
          <InlineText weight="medium" color="primary">
            No service can start without full approval.
          </InlineText>
        </Trans>
      ) : (
        <Trans id="analysis-confirmation-modal.quote-validation.after-sales">
          The{' '}
          <InlineText weight="medium" color="primary">
            after-sales team
          </InlineText>{' '}
          will review the quote.{' '}
          <InlineText weight="medium" color="primary">
            No service can start without full approval.
          </InlineText>
        </Trans>
      );
    }

    if (isClientValidationRequired && !isSkipValidationsToggled) {
      return (
        <Trans id="analysis-confirmation-modal.quote-validation.client">
          The{' '}
          <InlineText weight="medium" color="primary">
            client
          </InlineText>{' '}
          will review the quote.{' '}
          <InlineText weight="medium" color="primary">
            No service can start without full approval.
          </InlineText>
        </Trans>
      );
    }

    return (
      <Trans id="analysis-confirmation-modal.quote-validation.none">
        No review will be required on this quote.
      </Trans>
    );
  };

  return (
    <Paragraph
      size="100"
      color="secondary"
      style={{
        padding: '8px 12px',
        borderRadius: '8px',
        backgroundColor: 'var(--color-neutral-100)',
      }}
    >
      {getMessage()}
    </Paragraph>
  );
};

const SkipValidationToggle = ({
  skipValidations,
  isSkipValidationsToggled,
  setIsSkipValidationsToggled,
}: {
  skipValidations: {
    shown: boolean;
    enabled: boolean;
    disabledReasons: SkipValidationsDisabledReason[];
  };
  isSkipValidationsToggled: boolean;
  setIsSkipValidationsToggled: (value: boolean) => void;
}) => {
  const { t } = useLingui();

  if (!skipValidations.shown) {
    return;
  }

  const isDisabled = !skipValidations.enabled;

  const SKIP_VALIDATION_DISABLED_REASONS = {
    'cannot-skip-based-on-client-price': t({
      id: 'analysis-confirmation-modal.quote-validation.skip-validation.cannot-skip-based-on-client-price',
      message: 'Cannot skip based on client price',
    }),
    'cannot-skip-based-on-organization-price': t({
      id: 'analysis-confirmation-modal.quote-validation.skip-validation.cannot-skip-based-on-organization-price',
      message: 'Cannot skip based on organization price',
    }),
    'service-choice-changed': t({
      id: 'analysis-confirmation-modal.quote-validation.skip-validation.service-choice-changed',
      message: 'Cannot skip because service choice changed',
    }),
    'defects-changed': t({
      id: 'analysis-confirmation-modal.quote-validation.skip-validation.defects-changed',
      message: 'Cannot skip because defects have been updated',
    }),
  } satisfies Record<SkipValidationsDisabledReason, string>;

  if (isDisabled) {
    const disabledReasons = (
      <Stack>
        {skipValidations.disabledReasons.map((disabledReason, key) => (
          <div key={key}>{SKIP_VALIDATION_DISABLED_REASONS[disabledReason]}</div>
        ))}
      </Stack>
    );

    return (
      <Tooltip content={disabledReasons}>
        <Button variant="style-less">
          <Toggle
            label={t({
              id: 'analysis-confirmation-modal.skip-validation.label',
              message: 'Skip validation',
            })}
            isDisabled
            isSelected={false}
          />
        </Button>
      </Tooltip>
    );
  }

  return (
    <Toggle
      label={t({
        id: 'analysis-confirmation-modal.skip-validation.label',
        message: 'Skip validation',
      })}
      isSelected={isSkipValidationsToggled}
      onChange={(value) => setIsSkipValidationsToggled(value)}
    />
  );
};
